import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Trash2, Loader2, Upload, ExternalLink } from "lucide-react";

const SOCIAL_PLATFORMS = ["instagram", "facebook", "tiktok", "youtube", "linkedin", "pinterest", "whatsapp", "x"];
const fileUrl = (u) => (!u ? u : u.startsWith("http") ? u : `${API}${u.startsWith("/") ? "" : "/"}${u}`);

export default function AdminSettings() {
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/site-settings").then(({ data }) => setS(data))
      .catch(() => { toast.error("Impossible de charger les réglages."); navigate("/admin/content"); });
  }, [navigate]);

  const set = (patch) => setS((p) => ({ ...p, ...patch }));
  // Functional array helpers so async uploads (resolving later) never clobber concurrent edits.
  const updateRow = (field, i, patch) =>
    setS((p) => ({ ...p, [field]: (p[field] || []).map((x, j) => (j === i ? { ...x, ...patch } : x)) }));
  const removeRow = (field, i) =>
    setS((p) => ({ ...p, [field]: (p[field] || []).filter((_, j) => j !== i) }));
  const addRow = (field, item) =>
    setS((p) => ({ ...p, [field]: [...(p[field] || []), item] }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        description: s.description, contact_email: s.contact_email, contact_note: s.contact_note,
        socials: (s.socials || []).filter((x) => x.url),
        // Keep any legal doc that has a PDF; label is optional (defaulted) so an uploaded
        // doc without a typed label is never silently dropped on save.
        legal_docs: (s.legal_docs || []).filter((x) => x.url).map((x) => ({
          label: (x.label || "").trim() || "Document légal", url: x.url,
        })),
        partners: (s.partners || []).filter((x) => x.logo_url).map((x) => ({
          name: x.name || "", logo_url: x.logo_url, url: (x.url || "").trim(),
        })),
      };
      const { data } = await api.put("/admin/site-settings", payload);
      setS(data);
      toast.success("Réglages enregistrés.");
    } catch { toast.error("Échec de l'enregistrement."); }
    finally { setSaving(false); }
  };

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/site-settings/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  if (!s) return <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#96836B]" /></div>;

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#1A1B18]">
      <Nav />
      <div className="max-w-[1000px] mx-auto px-6 md:px-12 pt-28 pb-24">
        <div className="flex items-center justify-between gap-4 border-b border-[#EAE8E3] pb-6">
          <div>
            <button onClick={() => navigate("/admin/content")} className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-[#737373] hover:text-[#1A1B18]"><ArrowLeft className="h-3.5 w-3.5" /> Centre de contenu</button>
            <h1 className="font-serif-display text-4xl mt-3">Réglages du site</h1>
            <p className="text-sm text-[#737373] mt-1">Pied de page : réseaux sociaux, documents légaux et partenaires.</p>
          </div>
          <Button onClick={save} disabled={saving} data-testid="settings-save" className="rounded-none bg-[#1A1B18] hover:bg-[#96836B] text-white h-10 text-[10px] uppercase tracking-[0.22em]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-2" /> Enregistrer</>}
          </Button>
        </div>

        <div className="space-y-8 mt-8">
          {/* Collaborateurs (comptes admin) */}
          <Collaborators />

          {/* Général */}
          <section className="bg-white border border-[#EAE8E3] p-6 space-y-4" data-testid="settings-general">
            <p className="label-eyebrow">Général</p>
            <div><Label className="label-eyebrow">Description</Label><Textarea value={s.description || ""} onChange={(e) => set({ description: e.target.value })} className="luxury-input min-h-[80px] mt-1.5" data-testid="settings-description" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="label-eyebrow">Email de contact</Label><Input value={s.contact_email || ""} onChange={(e) => set({ contact_email: e.target.value })} className="luxury-input mt-1.5" /></div>
              <div><Label className="label-eyebrow">Note contact</Label><Input value={s.contact_note || ""} onChange={(e) => set({ contact_note: e.target.value })} className="luxury-input mt-1.5" /></div>
            </div>
          </section>

          {/* Réseaux sociaux */}
          <section className="bg-white border border-[#EAE8E3] p-6 space-y-3" data-testid="settings-socials">
            <p className="label-eyebrow">Réseaux sociaux</p>
            {(s.socials || []).map((soc, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`social-row-${i}`}>
                <select value={soc.platform || "instagram"} onChange={(e) => updateRow("socials", i, { platform: e.target.value })} className="luxury-input w-40 h-10 text-sm px-3 py-2 leading-normal bg-white">
                  {SOCIAL_PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <Input value={soc.url || ""} onChange={(e) => updateRow("socials", i, { url: e.target.value })} className="luxury-input flex-1" placeholder="https://instagram.com/dealtravel" />
                <button onClick={() => removeRow("socials", i)} className="text-[#737373] hover:text-[#7A4B3B]"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <Button onClick={() => addRow("socials", { platform: "instagram", url: "" })} variant="outline" className="rounded-none text-[10px] uppercase tracking-[0.2em]" data-testid="add-social"><Plus className="h-3 w-3 mr-1.5" /> Ajouter un réseau</Button>
          </section>

          {/* Documents légaux */}
          <section className="bg-white border border-[#EAE8E3] p-6 space-y-3" data-testid="settings-legal">
            <p className="label-eyebrow">Documents légaux (PDF)</p>
            {(s.legal_docs || []).map((d, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`legal-row-${i}`}>
                <Input value={d.label || ""} onChange={(e) => updateRow("legal_docs", i, { label: e.target.value })} className="luxury-input w-56" placeholder="Mentions légales" />
                {d.url ? (
                  <a href={fileUrl(d.url)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-[#96836B] hover:text-[#1A1B18] flex-1 truncate"><ExternalLink className="h-3.5 w-3.5" /> Voir le PDF</a>
                ) : <span className="text-[11px] text-[#737373] flex-1">Aucun fichier</span>}
                <FileButton accept="application/pdf" onFile={async (f) => { try { const r = await upload(f); updateRow("legal_docs", i, { url: r.url }); toast.success("PDF ajouté — pensez à Enregistrer."); } catch { toast.error("Échec upload."); } }} label="PDF" />
                <button onClick={() => removeRow("legal_docs", i)} className="text-[#737373] hover:text-[#7A4B3B]"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <Button onClick={() => addRow("legal_docs", { label: "", url: "" })} variant="outline" className="rounded-none text-[10px] uppercase tracking-[0.2em]" data-testid="add-legal"><Plus className="h-3 w-3 mr-1.5" /> Ajouter un document</Button>
          </section>

          {/* Partenaires */}
          <section className="bg-white border border-[#EAE8E3] p-6 space-y-3" data-testid="settings-partners">
            <p className="label-eyebrow">Badges partenaires</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {(s.partners || []).map((p, i) => (
                <div key={i} className="border border-[#EAE8E3] p-3 space-y-2" data-testid={`partner-row-${i}`}>
                  <div className="h-16 flex items-center justify-center bg-[#FAF9F6]">
                    {p.logo_url ? <img src={fileUrl(p.logo_url)} alt={p.name || "Partenaire"} className="max-h-14 w-auto object-contain" /> : <span className="text-[10px] text-[#737373]">Aucun logo</span>}
                  </div>
                  <Input value={p.name || ""} onChange={(e) => updateRow("partners", i, { name: e.target.value })} className="luxury-input h-8 text-xs" placeholder="Nom" />
                  <Input value={p.url || ""} onChange={(e) => updateRow("partners", i, { url: e.target.value })} className="luxury-input h-8 text-xs" placeholder="https://site-partenaire.com" data-testid={`partner-url-${i}`} />
                  <div className="flex items-center justify-between">
                    <FileButton accept="image/png,image/jpeg,image/webp" onFile={async (f) => { try { const r = await upload(f); updateRow("partners", i, { logo_url: r.url }); toast.success("Logo ajouté — pensez à Enregistrer."); } catch { toast.error("Échec upload."); } }} label="Logo" />
                    <button onClick={() => removeRow("partners", i)} className="text-[#737373] hover:text-[#7A4B3B]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={() => addRow("partners", { name: "", logo_url: "", url: "" })} variant="outline" className="rounded-none text-[10px] uppercase tracking-[0.2em]" data-testid="add-partner"><Plus className="h-3 w-3 mr-1.5" /> Ajouter un partenaire</Button>
          </section>
        </div>
      </div>
    </div>
  );
}

const FileButton = ({ accept, onFile, label }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f) { setBusy(true); await onFile(f); setBusy(false); } e.target.value = ""; }} />
      <button onClick={() => ref.current?.click()} disabled={busy} className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] border border-[#1A1B18] px-3 py-1.5 hover:bg-[#1A1B18] hover:text-white transition-colors">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} {label}
      </button>
    </>
  );
};

const Collaborators = () => {
  const { logout } = useAuth();
  const [users, setUsers] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [creating, setCreating] = useState(false);
  const [edit, setEdit] = useState(null); // { id, email, password }

  const load = () => api.get("/admin/auth/users").then(({ data }) => setUsers(data)).catch(() => toast.error("Chargement des collaborateurs impossible."));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!email.trim() || password.length < 8) { toast.error("Email valide + mot de passe (8 caractères min.) requis."); return; }
    setCreating(true);
    try {
      await api.post("/admin/auth/users", { email: email.trim(), password });
      toast.success("Collaborateur créé.");
      setEmail(""); setPassword(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Création impossible."); }
    finally { setCreating(false); }
  };

  const saveEdit = async () => {
    const patch = {};
    if (edit.email && edit.email !== edit.origEmail) patch.email = edit.email.trim();
    if (edit.password) { if (edit.password.length < 8) { toast.error("Mot de passe : 8 caractères min."); return; } patch.password = edit.password; }
    if (!Object.keys(patch).length) { setEdit(null); return; }
    try {
      const { data } = await api.patch(`/admin/auth/users/${edit.id}`, patch);
      toast.success("Collaborateur mis à jour.");
      setEdit(null);
      if (data.requires_relogin) { toast.info("Votre email a changé — reconnectez-vous."); setTimeout(logout, 1200); return; }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Mise à jour impossible."); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Supprimer le collaborateur ${u.email} ?`)) return;
    try { await api.delete(`/admin/auth/users/${u.id}`); toast.success("Collaborateur supprimé."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible."); }
  };

  return (
    <section className="bg-white border border-[#EAE8E3] p-6 space-y-4" data-testid="settings-collaborators">
      <div>
        <p className="label-eyebrow">Collaborateurs</p>
        <p className="text-xs text-[#737373] mt-1">Comptes ayant accès à l'espace d'administration. Vous pouvez créer, modifier et supprimer des accès, y compris le vôtre.</p>
      </div>

      <div className="divide-y divide-[#EAE8E3] border border-[#EAE8E3]">
        {users === null ? (
          <div className="p-4 flex items-center gap-2 text-sm text-[#737373]"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div>
        ) : users.map((u) => (
          <div key={u.id} className="p-4" data-testid={`collab-row-${u.id}`}>
            {edit?.id === u.id ? (
              <div className="space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} className="luxury-input h-9 text-sm" placeholder="Email" data-testid="collab-edit-email" />
                  <Input type="password" value={edit.password} onChange={(e) => setEdit({ ...edit, password: e.target.value })} className="luxury-input h-9 text-sm" placeholder="Nouveau mot de passe (optionnel)" data-testid="collab-edit-password" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={saveEdit} className="rounded-none bg-[#1A1B18] hover:bg-[#96836B] text-white h-8 text-[10px] uppercase tracking-[0.2em]" data-testid="collab-save-edit">Enregistrer</Button>
                  <Button onClick={() => setEdit(null)} variant="outline" className="rounded-none h-8 text-[10px] uppercase tracking-[0.2em]">Annuler</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-[#1A1B18] truncate">{u.email}
                    {u.is_self && <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-[#96836B]">Vous</span>}
                    {u.protected && <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-[#737373]">Principal</span>}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button onClick={() => setEdit({ id: u.id, email: u.email, origEmail: u.email, password: "" })} className="text-[11px] uppercase tracking-[0.18em] text-[#96836B] hover:text-[#1A1B18]" data-testid={`collab-edit-${u.id}`}>Modifier</button>
                  {!u.is_self && !u.protected && (
                    <button onClick={() => remove(u)} className="text-[#737373] hover:text-[#7A4B3B]" data-testid={`collab-delete-${u.id}`}><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="border-t border-[#EAE8E3] pt-4 space-y-2">
        <p className="label-eyebrow text-[#96836B]">Nouveau collaborateur</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} className="luxury-input h-9 text-sm" placeholder="email@dealtravel.eu" data-testid="collab-new-email" />
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="luxury-input h-9 text-sm" placeholder="Mot de passe (8 caractères min.)" data-testid="collab-new-password" />
        </div>
        <Button onClick={create} disabled={creating} className="rounded-none bg-[#1A1B18] hover:bg-[#96836B] text-white h-9 text-[10px] uppercase tracking-[0.22em]" data-testid="collab-create">
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-3.5 w-3.5 mr-1.5" /> Créer le collaborateur</>}
        </Button>
      </div>
    </section>
  );
};

